/**
 * Procedures from the shipped timelines (public/data/timelines/<id>.json,
 * section 3 of appendix B) mapped to Procedure objects with the choreography
 * the Unity Timeline used to supply: per step, the subsystems named in the
 * step (its Unity scene alias plus labels found in the text) are highlighted,
 * the camera goes to the first of them, container_* scenes switch to the
 * container subview, the signal-flow procedures run in x-ray with the
 * receive or transmit path, each step dwells DEFAULT_DWELL_MS when playing.
 * Steps that switch to another asset (the Data Flow) highlight what they name
 * on that asset, read from its element file. A pre-authored
 * public/data/twin/procedures/<id>.json wins when present.
 */
import type { Procedure, ProcedureStep, Subsystem, TrainingImage, ViewMode } from '../../types';
import { htmlToText } from '../lib/sanitizeHtml';

export const TIMELINE_IDS = [
  'TimelineAntennaFlow',
  'TimelineAntennaFlowTransmit',
  'TimelineAntennaMaintenance',
  'TimelineDataFlow',
] as const;
export type TimelineId = (typeof TIMELINE_IDS)[number];

export const DEFAULT_DWELL_MS = 6000;
const MAX_HIGHLIGHTS = 6;

export interface TimelineStepJson {
  step?: number;
  title?: string;
  description: string;
  warning?: string;
  scene?: string;
  training?: TrainingImage[];
}

export interface TimelineJson {
  name: string;
  type: 'procedure' | 'timeline';
  initialDescription?: string;
  totalSteps?: number;
  steps: TimelineStepJson[];
}

/** Unity camera scene names used by the shipped timelines, to the rf_antenna subsystem ids they show. */
export const SCENE_SUBSYSTEM_ALIASES: Record<string, string[]> = {
  // The first and last steps of the antenna flows: the signal reaching (or leaving) the dish and its feed.
  satellite: ['primary_reflector', 'feed_horn'],
  antenna_exterior: ['primary_reflector', 'main_antenna'],
  feed_horn: ['feed_horn'],
  waveguide: ['waveguide'],
  antenna_hub_lna: ['low_noise_amplifier'],
  antenna_hub_hpa: ['hpa', 'feed_horn'],
  container_rx_rack: ['container_building'],
  container_tx_rack: ['container_building'],
  container_spectrum_analyzer: ['spectrum_analyzer'],
  container_freq_translator: ['freq_translator'],
  container_ifl: ['ifl_switch'],
  container_if_dist: ['if_distribution'],
  container_satellite_modem: ['modem', 'bit_synchronizer'],
  container_mission_link_modem: ['decoder'],
  container_switch: ['switch_matrix'],
  network_output: ['switch_matrix', 'storage_servers'],
  network_input: ['switch_matrix'],
};

/**
 * Scenes framed wider than their first highlight: the network steps of the
 * antenna flows run in the main view, where a close-up of one rack unit puts
 * the camera against the packet tube; they show the whole container (seen
 * through in x-ray) with the units highlighted inside instead.
 */
export const SCENE_CAMERA: Record<string, string> = {
  network_output: 'container_building',
  network_input: 'container_building',
};

/** Scenes that name another asset (the cross-asset Data Flow procedure). */
export const SCENE_ASSETS = new Set([
  'senseSat_01',
  'tdrs',
  'goes',
  'rf_antenna',
  'optical_antenna',
  'ground_station',
  'data_center',
  'data_cloud',
  'soldier',
  'er_2',
  'boat',
  'terrestrial',
]);

/** Signal path direction per timeline id. */
export const TIMELINE_SIGNAL_PATH: Partial<Record<TimelineId, 'receive' | 'transmit'>> = {
  TimelineAntennaFlow: 'receive',
  TimelineAntennaFlowTransmit: 'transmit',
};

export interface ProcedureContext {
  assetId: string;
  subsystems: Subsystem[];
  /**
   * Subsystem trees of the other assets a cross-asset procedure visits (the
   * Data Flow), by asset id; loadProcedure fills it from their element files
   * so those steps highlight and frame what they name on that asset.
   */
  otherAssets?: Record<string, Subsystem[]>;
}

interface Candidate {
  id: string;
  needles: string[];
}

/**
 * Words that name a subsystem but mostly appear in the timelines as plain
 * prose ("environmental monitoring information", "Environmental conditions
 * must be ..."): a mention in the step text does not highlight that
 * subsystem (a scene alias still can).
 */
export const GENERIC_NEEDLES = new Set(['environmental']);

function candidates(subsystems: Subsystem[]): Candidate[] {
  const out: Candidate[] = [];
  const visit = (s: Subsystem) => {
    const needles = new Set<string>();
    needles.add(s.id.replace(/_/g, ' ').toLowerCase());
    const label = s.label.toLowerCase();
    needles.add(label);
    const bare = label.replace(/\s*\(.*?\)\s*/g, ' ').trim();
    if (bare.length >= 4) needles.add(bare);
    const paren = /\(([^)]+)\)/.exec(label);
    if (paren && paren[1].length >= 3) needles.add(paren[1].trim());
    out.push({ id: s.id, needles: [...needles].filter((n) => n.length >= 3 && !GENERIC_NEEDLES.has(n)) });
    for (const child of s.childs ?? []) visit(child);
  };
  for (const s of subsystems) visit(s);
  return out;
}

/** Subsystem ids whose id or label appears in the text, ordered by first occurrence; at the same position the longer match ("acu cooling fan" over "acu") comes first. */
export function findMentionedSubsystems(text: string, subsystems: Subsystem[]): string[] {
  const haystack = ` ${text.toLowerCase().replace(/[^a-z0-9/&]+/g, ' ')} `;
  const hits: Array<{ id: string; at: number; length: number }> = [];
  for (const c of candidates(subsystems)) {
    let best = -1;
    let length = 0;
    for (const needle of c.needles) {
      const at = haystack.indexOf(` ${needle} `);
      if (at === -1) continue;
      if (best === -1 || at < best || (at === best && needle.length > length)) {
        best = at;
        length = needle.length;
      }
    }
    if (best !== -1) hits.push({ id: c.id, at: best, length });
  }
  return hits.sort((a, b) => a.at - b.at || b.length - a.length).map((h) => h.id);
}

export function stepText(step: Pick<TimelineStepJson, 'title' | 'description'>): string {
  return `${step.title ?? ''} ${htmlToText(step.description)}`.trim();
}

/** Training image paths are relative to the previous game folder ("images/x.jpg"); the files live under /images. */
export function resolveTrainingImage(path: string): string {
  if (!path) return '';
  if (/^(https?:)?\/\//i.test(path) || path.startsWith('/') || path.startsWith('data:')) return path;
  return `/${path.replace(/^\.?\//, '')}`;
}

export function subviewHintFor(scene: string | undefined): string | undefined {
  if (!scene) return undefined;
  if (scene.startsWith('container_')) return 'container';
  if (scene.includes('room')) return 'lower-room';
  return undefined;
}

/** Choreography defaults for one step. */
export function choreograph(
  step: TimelineStepJson,
  id: string,
  ctx: ProcedureContext,
): Pick<
  ProcedureStep,
  'camera' | 'highlight' | 'assetId' | 'subview' | 'viewMode' | 'signalPathId' | 'dwellMs'
> {
  const scene = step.scene;
  const crossAsset = scene && SCENE_ASSETS.has(scene) ? scene : undefined;
  // The subsystems the step can name: its own asset's, or the visited asset's when the context carries them.
  const tree = !crossAsset || crossAsset === ctx.assetId ? ctx.subsystems : ctx.otherAssets?.[crossAsset];
  const known = new Set<string>();
  const visit = (s: Subsystem) => {
    known.add(s.id);
    for (const child of s.childs ?? []) visit(child);
  };
  for (const s of tree ?? []) visit(s);

  const highlight: string[] = [];
  if (scene && !crossAsset) {
    for (const alias of SCENE_SUBSYSTEM_ALIASES[scene] ?? [scene])
      if (known.has(alias) && !highlight.includes(alias)) highlight.push(alias);
  }
  if (tree) {
    for (const mentioned of findMentionedSubsystems(stepText(step), tree)) {
      if (!highlight.includes(mentioned)) highlight.push(mentioned);
    }
  }
  const trimmed = highlight.slice(0, MAX_HIGHLIGHTS);
  const direction = TIMELINE_SIGNAL_PATH[id as TimelineId];
  const wide = scene && !crossAsset ? SCENE_CAMERA[scene] : undefined;
  const cameraId = wide && known.has(wide) && trimmed.length ? wide : trimmed[0];
  const viewMode: ViewMode | undefined = direction
    ? 'xray'
    : id === 'TimelineAntennaMaintenance'
      ? 'solid'
      : undefined;
  return {
    camera: cameraId ? { subsystemId: cameraId } : undefined,
    highlight: trimmed,
    assetId: crossAsset,
    subview: subviewHintFor(scene),
    viewMode,
    signalPathId: direction,
    dwellMs: DEFAULT_DWELL_MS,
  };
}

/** A shipped timeline as a Procedure with choreography defaults on every step. */
export function mapTimelineToProcedure(id: string, json: TimelineJson, ctx: ProcedureContext): Procedure {
  const steps: ProcedureStep[] = json.steps.map((step, index) => ({
    step: step.step ?? index + 1,
    title: step.title,
    description: step.description,
    warning: step.warning,
    scene: step.scene,
    training: step.training?.map((t) => ({ ...t, image: resolveTrainingImage(t.image) })),
    ...choreograph(step, id, ctx),
  }));
  return {
    id,
    name: json.name,
    type: json.type === 'timeline' ? 'timeline' : 'procedure',
    initialDescription: json.initialDescription,
    totalSteps: json.totalSteps ?? steps.length,
    assetId: ctx.assetId,
    steps,
  };
}

/** Fills choreography defaults into a pre-authored procedure where a step omits them. */
export function withDefaults(procedure: Procedure, ctx: ProcedureContext): Procedure {
  return {
    ...procedure,
    totalSteps: procedure.totalSteps || procedure.steps.length,
    steps: procedure.steps.map((step) => {
      const defaults = choreograph(step, procedure.id, ctx);
      return {
        ...step,
        camera: step.camera ?? defaults.camera,
        highlight: step.highlight ?? defaults.highlight,
        assetId: step.assetId ?? defaults.assetId,
        subview: step.subview ?? defaults.subview,
        viewMode: step.viewMode ?? defaults.viewMode,
        signalPathId: step.signalPathId === undefined ? defaults.signalPathId : step.signalPathId,
        dwellMs: step.dwellMs ?? defaults.dwellMs,
        training: step.training?.map((t) => ({ ...t, image: resolveTrainingImage(t.image) })),
      };
    }),
  };
}

function isProcedureJson(value: unknown): value is Procedure {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as Procedure).steps) &&
    typeof (value as Procedure).id === 'string'
  );
}

function isTimelineJson(value: unknown): value is TimelineJson {
  return (
    !!value &&
    typeof value === 'object' &&
    Array.isArray((value as TimelineJson).steps) &&
    typeof (value as TimelineJson).name === 'string'
  );
}

/**
 * A JSON file answered the request. Static Assets runs with the single-page
 * fallback (wrangler.jsonc not_found_handling), and Vite's dev server does the
 * same: a missing file comes back as the app shell, 200 with text/html.
 */
function servedJson(res: Response): boolean {
  return res.ok && /\bjson\b/i.test(res.headers.get('content-type') ?? '');
}

/**
 * The subsystem trees of the other assets the steps switch to (their scene
 * names an asset), from /data/elements/<asset>.json. Throws with the URL when
 * one cannot be read.
 */
async function otherAssetTrees(
  scenes: Array<string | undefined>,
  ctx: ProcedureContext,
  fetchImpl: typeof fetch,
): Promise<Record<string, Subsystem[]>> {
  const ids = [
    ...new Set(scenes.filter((sc): sc is string => !!sc && SCENE_ASSETS.has(sc) && sc !== ctx.assetId)),
  ];
  const trees: Record<string, Subsystem[]> = {};
  for (const assetId of ids) {
    const url = `/data/elements/${assetId}.json`;
    const res = await fetchImpl(url);
    if (!servedJson(res))
      throw new Error(
        `Procedure step asset ${assetId}: HTTP ${res.status} (${res.headers.get('content-type') ?? 'no content type'}) for ${url}`,
      );
    const json = (await res.json()) as { subsystems?: unknown };
    if (!Array.isArray(json.subsystems))
      throw new Error(`Procedure step asset ${assetId}: ${url} has no subsystems array`);
    trees[assetId] = json.subsystems as Subsystem[];
  }
  return trees;
}

/** Loads /data/twin/procedures/<id>.json when the scene build authored one, else maps /data/timelines/<id>.json. */
export async function loadProcedure(
  id: string,
  ctx: ProcedureContext,
  fetchImpl: typeof fetch = fetch,
): Promise<Procedure> {
  const withOthers = async (scenes: Array<string | undefined>): Promise<ProcedureContext> => ({
    ...ctx,
    otherAssets: { ...(await otherAssetTrees(scenes, ctx, fetchImpl)), ...ctx.otherAssets },
  });
  // Offline, an authored file that was never shipped is not in the precache, so the probe rejects instead of
  // answering 404; the shipped timeline is precached, so the fallback still works.
  let authored: Response | null;
  try {
    authored = await fetchImpl(`/data/twin/procedures/${id}.json`);
  } catch (err) {
    console.warn(`[procedures] the authored-file probe for ${id} failed; using the shipped timeline:`, err);
    authored = null;
  }
  if (authored && servedJson(authored)) {
    const json: unknown = await authored.json();
    if (isProcedureJson(json)) return withDefaults(json, await withOthers(json.steps.map((st) => st.scene)));
    if (isTimelineJson(json))
      return mapTimelineToProcedure(id, json, await withOthers(json.steps.map((st) => st.scene)));
    throw new Error(`Procedure ${id}: unrecognised JSON shape in /data/twin/procedures`);
  }
  const shipped = await fetchImpl(`/data/timelines/${id}.json`);
  if (!shipped.ok) throw new Error(`Procedure ${id}: HTTP ${shipped.status} from /data/timelines/${id}.json`);
  if (!servedJson(shipped))
    throw new Error(
      `Procedure ${id}: /data/timelines/${id}.json is missing (the server answered ${shipped.headers.get('content-type') ?? 'without a content type'})`,
    );
  const json: unknown = await shipped.json();
  if (!isTimelineJson(json)) throw new Error(`Procedure ${id}: unrecognised timeline JSON`);
  return mapTimelineToProcedure(id, json, await withOthers(json.steps.map((st) => st.scene)));
}
